import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo, timeline, testimonials, serviceFeatures } from "@/mocks/home";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const formUrl = "https://form.readdy.ai/submit/91cn-contact";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch(formUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(Object.fromEntries(formData as unknown as Record<string, string>)).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || "提交失败，请稍后重试";
        setFormError(serverMsg.includes("spam") || serverMsg.includes("form data is spam") ? "提交失败，请稍后重试" : serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "关于我们" },
              ]}
            />
            <div className="px-4 md:px-6 pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                关于 91.cn
              </h1>
              <p className="text-sm text-foreground-600 max-w-3xl">
                了解 91.cn 的品牌故事、发展历程与核心团队
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  91.cn 成立于 2018 年，是一家专注于互联网服务的创新型科技平台。我们秉承「让互联网服务更简单」的使命，
                  致力于为个人用户和企业客户提供高效、安全、便捷的一站式互联网解决方案。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  经过多年的深耕与发展，91.cn 已经从一个单一功能的工具平台，成长为涵盖数据管理、智能分析、协作办公、
                  API 服务、安全防护等八大核心模块的综合性互联网服务平台。我们的服务覆盖全国 300 多个城市，累计服务用户超过 1000 万。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  91.cn 始终坚持技术创新与用户至上的理念。我们拥有一支由行业顶尖人才组成的研发团队，每月持续迭代产品功能，
                  确保平台始终保持在技术前沿。同时，我们建立了完善的客户服务体系，7×24 小时为用户提供专业的支持与服务。
                </p>
              </div>
              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20warm%20ambient%20lighting%2C%20clean%20workspace%20design%2C%20geometric%20architectural%20elements%2C%20soft%20beige%20and%20cream%20color%20palette%2C%20professional%20corporate%20atmosphere%2C%20no%20people%2C%20high%20quality%20interior%20photography&width=700&height=500&seq=about-brand-2026&orientation=landscape"
                  alt="91.cn 品牌形象"
                  title="91.cn 品牌形象"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Platform Advantages */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                平台优势
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                六大核心优势，打造行业领先的互联网服务平台
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {serviceFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg p-6 md:p-7 border border-background-200"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${feature.icon} text-lg`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                发展历程
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                见证 91.cn 从起步到行业标杆的成长之路
              </p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />
              <div className="flex flex-col gap-8">
                {timeline.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="md:w-1/2 pl-10 md:pl-0 md:px-8">
                      <div className="bg-background-100 rounded-lg p-5 border border-background-200">
                        <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 mb-2 whitespace-nowrap">
                          {item.year}
                        </span>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 top-5 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 ring-4 ring-background-50 z-10" />
                    <div className="md:w-1/2 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                来自真实用户的信赖与认可
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg p-6 md:p-7 border border-background-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">{item.title}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    &ldquo;{item.content}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                如有任何疑问或合作需求，欢迎随时与我们取得联系
              </p>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">客服热线</div>
                    <div className="text-sm font-semibold text-foreground-900">{siteInfo.phone}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">电子邮箱</div>
                    <div className="text-sm font-semibold text-foreground-900">{siteInfo.email}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">公司地址</div>
                    <div className="text-sm font-semibold text-foreground-900">{siteInfo.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-wechat-line text-lg" />
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">微信公众号</div>
                    <div className="text-sm font-semibold text-foreground-900">{siteInfo.social.wechat}</div>
                  </div>
                </div>
              </div>

              <div className="border-t border-background-200 pt-8">
                <h3 className="text-base font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="" id="contact-form-about">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900 transition-colors duration-200"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 text-foreground-900 resize-none transition-colors duration-200"
                      placeholder="请输入您的消息内容（最多500字）"
                    />
                    <div className="text-xs text-foreground-500 mt-1">最多可输入 500 个字符</div>
                  </div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-8 py-3 text-sm font-medium whitespace-nowrap cursor-pointer rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200"
                  >
                    发送消息
                  </button>
                  {formStatus === "success" && (
                    <p className="mt-4 text-sm text-accent-700 bg-accent-50 px-4 py-2.5 rounded-md">
                      您的消息已成功发送，我们将在 24 小时内与您联系。感谢您的关注！
                    </p>
                  )}
                  {formStatus === "error" && (
                    <p className="mt-4 text-sm text-red-700 bg-red-50 px-4 py-2.5 rounded-md">
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于 91.cn",
          "description": "了解 91.cn 的品牌故事、发展历程与核心团队",
          "url": "https://www.91.cn/about",
          "isPartOf": {
            "@type": "WebSite",
            "name": "91.cn",
            "url": "https://www.91.cn"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.91.cn"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "关于我们"
            }
          ]
        })}
      </script>
    </>
  );
}